import React, { useCallback, useState, useEffect } from "react";
import { useFetcher, useInvalidator } from "@rest-hooks/core";
import {
	Grid,
	Paper,
	Button,
	List,
	ListItem,
	ListItemSecondaryAction,
	Checkbox,
	ListItemText,
	ListItemIcon
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import {
	ApplicationSignerResource,
	SigningDocumentResource,
	ApplicationSigner,
	useResourceWithInvalidate
} from "@ploy-lib/rest-resources";
import { AppSignerForm } from "./AppSignerForm";
import {
	FormattedMessage,
	injectIntl,
	WrappedComponentProps,
	defineMessages
} from "react-intl";
import { EsignType } from "@ploy-lib/types";

export interface AppSignerListProps {
	applicationNumber: string;
	documents: SigningDocumentResource[];
	vendor?: boolean;
	supplier?: boolean;
	ssnServiceEnabled: boolean;
	esignType: number;
	selfEmployedCustomer?: boolean;
	onEdit?: (selected: boolean) => void;
	canAddAndDelete: boolean;
	canChooseSigners: boolean;
	canEditIndividualDocuments: boolean;
	showValidPeriod?: boolean;
	disabled: boolean;
	mustSignDefaultChecked: boolean;
	guarantorId?: number;
	hideDocumentsToSign?: boolean;
	signerType: number;
}

const messages = defineMessages({
	addSigner: {
		id: "core.application-signing.signer-list.add-new",
		defaultMessage: "Add new signing subject"
	}
});

const AppSignerList = injectIntl(
	(props: AppSignerListProps & WrappedComponentProps) => {
		const {
			applicationNumber,
			documents,
			intl,
			onEdit,
			ssnServiceEnabled,
			esignType,
			selfEmployedCustomer = false,
			showValidPeriod,
			canAddAndDelete,
			canChooseSigners,
			canEditIndividualDocuments,
			disabled,
			mustSignDefaultChecked,
			guarantorId,
			hideDocumentsToSign,
			signerType
		} = props;

		const signers = useResourceWithInvalidate(
			ApplicationSignerResource.list(),
			{
				signerType,
				applicationNumber,
				customerId: guarantorId
			}
		);

		const updateSigner = useFetcher(ApplicationSignerResource.update());

		const [selected, setSelected] = useState<
			ApplicationSigner | null | undefined
		>(null);

		const goBack = useCallback(() => {
			setSelected(null);
		}, []);

		const refetchSigners = useFetcher(ApplicationSignerResource.list());
		const refetchDocuments = useFetcher(SigningDocumentResource.list());

		const onSuccess = useCallback(() => {
			refetchSigners({
				applicationNumber,
				signerType,
				customerId: guarantorId
			});
			refetchDocuments({ applicationNumber: applicationNumber });
			goBack();
		}, [
			refetchSigners,
			applicationNumber,
			signerType,
			guarantorId,
			refetchDocuments,
			goBack
		]);

		useEffect(
			() => onEdit && onEdit(selected != null || selected === undefined),
			[selected, onEdit]
		);

		const allDocsAreSigned = documents && documents.every(d => d.signed);
		const canAddSigner =
			!allDocsAreSigned &&
			!selfEmployedCustomer &&
			esignType !== EsignType.SsnNotRequired &&
			canAddAndDelete &&
			!disabled;
		const canEditDocuments = !selfEmployedCustomer;

		if (selected !== null) {
			return (
				<Paper elevation={0}>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<Button onClick={goBack}>
								<ChevronLeftIcon />
								<FormattedMessage
									id="core.application-signing.signer-list.go-back"
									defaultMessage="Back"
								/>
							</Button>
						</Grid>
						<Grid item xs={12}>
							<AppSignerForm
								applicationNumber={applicationNumber}
								signer={selected}
								documents={documents}
								disabled={allDocsAreSigned || disabled}
								onSuccess={onSuccess}
								canEditDocuments={canEditDocuments}
								canEditIndividualDocuments={canEditIndividualDocuments}
								esignType={esignType}
								ssnServiceEnabled={ssnServiceEnabled}
								showValidPeriod={showValidPeriod}
								mustSignDefaultChecked={mustSignDefaultChecked}
								guarantorId={guarantorId}
								hideDocumentsToSign={hideDocumentsToSign}
								signerType={signerType}
							/>
						</Grid>
					</Grid>
				</Paper>
			);
		}
		return (
			<Paper elevation={0}>
				<List dense={true}>
					{signers &&
						signers.map((signer, i) => (
							<ListItem
								key={
									signer.applicationSignerId
										? signer.applicationSignerId.toString()
										: i.toString()
								}
								button
								onClick={() => setSelected(signer)}
							>
								<ListItemIcon>
									<ChevronRightIcon />
								</ListItemIcon>
								<ListItemText
									primary={`${signer.lastName} ${signer.firstName}`}
									secondary={
										signer.dataValidationErrorMessage
											? signer.dataValidationErrorMessage
											: signer.role || " "
									}
									secondaryTypographyProps={
										signer.dataValidationErrorMessage
											? { color: "error" }
											: undefined
									}
								/>
								<ListItemSecondaryAction>
									{canChooseSigners ||
									(signer.mustSign && !selfEmployedCustomer) ? (
										<Checkbox
											color="primary"
											onChange={(
												event: React.ChangeEvent<HTMLInputElement>
											) => {
												if (canChooseSigners) {
													updateSigner(
														{ signerType: signerType },
														{
															...signer,
															mustSign: event.target.checked
														}
													).then(() => {
														onSuccess();
													});
												} else if (signer.mustSign) {
													updateSigner(
														{ signerType: signerType },
														{
															...signer,
															mustSign: false
														}
													).then(() => {
														onSuccess();
													});
												}
											}}
											checked={signer.mustSign}
											disabled={
												allDocsAreSigned ||
												Boolean(
													signer.applicationSignerId &&
														documents &&
														documents.some(
															d =>
																d.signed &&
																d.signers.includes(signer.applicationSignerId!)
														)
												) ||
												!signer.canSetMustSign
											}
										/>
									) : null}
								</ListItemSecondaryAction>
							</ListItem>
						))}
					{!canAddSigner ? null : (
						<ListItem button onClick={() => setSelected(undefined)}>
							<ListItemIcon>
								<AddIcon />
							</ListItemIcon>
							<ListItemText primary={intl.formatMessage(messages.addSigner)} />
						</ListItem>
					)}
				</List>
			</Paper>
		);
	}
);

AppSignerList.displayName = "DployAppSignerList";

export { AppSignerList };
